import {
  type ElementType,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { CurrencyFormateText } from "@/components/CurrencyFormat";
import { USER_ROLE } from "@/config/constants";
import PageLayout from "@/components/layouts/page/PageLayout";
import LocationListModal from "@/components/modals/LocationListModal";
import TableServer from "@/components/tables/TableServer";
import { GlobalContext } from "@/helpers/contexts/contexts";
import useCustomerPortal from "@/helpers/hooks/useCustomerPortal";
import useFetcher from "@/helpers/hooks/useFetcher";
import { getFormatDate } from "@/helpers/utils";
import { stripeMicroService } from "@/shared/services/stripeMicroService";

const PAGE_SIZE = 10;

type PaymentRecord = Record<string, any>;
type TableParams = {
  pageIndex: number;
  pageSize: number;
  searchKey?: string;
};
type GlobalContextValue = {
  loggedInUser?: PaymentRecord;
  loggedInUserRole?: string;
  selectedBrand?: PaymentRecord;
};

const PageLayoutComponent = PageLayout as ElementType;
const TableServerComponent = TableServer as ElementType;
const LocationListModalComponent = LocationListModal as ElementType;
const CurrencyFormateTextComponent = CurrencyFormateText as ElementType;

const PaymentTableText = ({ children }: { children: ReactNode }) => (
  <Box
    component="span"
    className="text-break"
    sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
  >
    {children}
  </Box>
);

const getPaymentLocations = (payment: PaymentRecord) => {
  return (payment?.location_details || []).map(
    (locationName: string, index: number) => ({
      id: `${payment?.id || "payment"}-${index}`,
      locationName,
    })
  );
};

const PaymentLocationCount = ({ payment }: { payment: PaymentRecord }) => {
  const locations = getPaymentLocations(payment);

  if (!locations.length) return 0;

  return (
    <LocationListModalComponent locations={locations} showStatus={false} />
  );
};

const PaymentsPage = () => {
  const { loggedInUser, loggedInUserRole, selectedBrand } = useContext(
    GlobalContext
  ) as GlobalContextValue;
  const [tableParams, setTableParams] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
    searchKey: "",
  });
  const { isLoadingCustomerPortal, navigateToCustomerPortal } =
    useCustomerPortal();

  const reqData = useMemo(() => {
    if (!selectedBrand?.id || !loggedInUser?.id) return null;

    return {
      brandId: selectedBrand.id,
      page: tableParams.pageIndex + 1,
      row: tableParams.pageSize,
      user_id: loggedInUser.id,
      search: tableParams.searchKey || "",
    };
  }, [
    loggedInUser?.id,
    selectedBrand?.id,
    tableParams.pageIndex,
    tableParams.pageSize,
    tableParams.searchKey,
  ]);

  const { data, error, isLoading, isValidating } = useFetcher(
    reqData,
    stripeMicroService.getPaymentsLedger,
    { revalidateOnFocus: false }
  );

  const paymentsData = data?.data?.data || data?.data?.result?.data || {};
  const payments = paymentsData?.payments || [];
  const totalRecords =
    paymentsData?.recordsFiltered ?? paymentsData?.recordsTotal ?? 0;
  const pageCount =
    paymentsData?.pageCount || Math.ceil(totalRecords / tableParams.pageSize);
  const isTableLoading = isLoading || isValidating;
  const canManagePayments =
    loggedInUserRole === USER_ROLE.PARTICIPANT.NAME &&
    !loggedInUser?.is_sub_participant;

  const handleFetchPayments = useCallback(
    ({ pageIndex, pageSize, searchKey }: TableParams) => {
      setTableParams((currentParams) => {
        const nextParams = {
          pageIndex,
          pageSize,
          searchKey: searchKey || "",
        };

        if (
          nextParams.pageIndex === currentParams.pageIndex &&
          nextParams.pageSize === currentParams.pageSize &&
          nextParams.searchKey === currentParams.searchKey
        ) {
          return currentParams;
        }

        return nextParams;
      });
    },
    []
  );

  const renderTableTitle = useCallback(
    () => (
      <div className="col">
        <Typography variant="h6" className="table-title mb-0">
          Payment Visibility & Report
        </Typography>
      </div>
    ),
    []
  );

  const columns = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "date",
        width: 120,
        Cell: ({ value }: { value: string }) => getFormatDate(value) || "---",
      },
      {
        Header: "Amount",
        accessor: "amount",
        width: 105,
        Cell: ({ value }: { value: number }) =>
          value || value === 0 ? (
            <CurrencyFormateTextComponent value={value} />
          ) : (
            "---"
          ),
      },
      {
        Header: "Location(s)",
        accessor: "location_count",
        width: 150,
        disableSortBy: true,
        Cell: ({ row }: { row: PaymentRecord }) => (
          <PaymentLocationCount payment={row.original} />
        ),
      },
      {
        Header: "Description",
        accessor: "description",
        width: 240,
        Cell: ({ value }: { value?: ReactNode }) => (
          <PaymentTableText>{value || "---"}</PaymentTableText>
        ),
      },
      {
        Header: "Payment Method",
        accessor: "payment_method",
        width: 165,
        Cell: ({ value }: { value?: ReactNode }) => (
          <PaymentTableText>{value || "---"}</PaymentTableText>
        ),
      },
      {
        Header: "Paid By",
        accessor: "paid_by",
        width: 180,
        Cell: ({ value }: { value?: ReactNode }) => (
          <PaymentTableText>{value || "---"}</PaymentTableText>
        ),
      },
    ],
    []
  );

  return (
    <PageLayoutComponent className="mx-0 mx-sm-2">
      <Box
        className="d-flex justify-content-between align-items-start mb-3 ml-2"
        sx={{
          flexDirection: { xs: "column", md: "row" },
          ".widget-title": { borderLeft: 0, pl: 0 },
        }}
      >
        <Box>
          <Typography variant="h5" className="widget-title mb-2">
            Payments
          </Typography>
          <Typography
            className="mb-2"
            sx={{ color: "var(--neutral-700)", fontSize: 13 }}
          >
            Manage your payments and see your recent payment history
          </Typography>
        </Box>
        {canManagePayments && (
          <Button
            variant="contained"
            color="primary"
            className="py-2 text-nowrap"
            endIcon={<OpenInNewIcon fontSize="small" />}
            onClick={navigateToCustomerPortal}
            disabled={isLoadingCustomerPortal}
          >
            Manage Payments
          </Button>
        )}
      </Box>

      <Card className="w-100 overflow-hidden px-0 py-4">
        <CardContent className="pt-0 pb-0 px-0">
          <TableServerComponent
            columns={columns}
            data={error ? [] : payments}
            isLoading={isTableLoading}
            pageCount={pageCount}
            totalRecords={totalRecords}
            initialPageSize={tableParams.pageSize}
            initialPage={tableParams.pageIndex}
            fetchData={handleFetchPayments}
            showPagination
            showPageSize={false}
            showSearch
            filters={renderTableTitle}
            hideSearchLabel
            showSkeletonLoader
            skeletonRowCount={5}
            manualSortBy={false}
            emptyState={
              <Alert
                severity={error ? "error" : "info"}
                className="my-0 text-left"
              >
                {error ? "Unable to load payments." : "No payments found."}
              </Alert>
            }
          />
        </CardContent>
      </Card>
    </PageLayoutComponent>
  );
};

export default PaymentsPage;
